import {
  Component,
  EventEmitter,
  inject,
  OnDestroy,
  Output,
  signal,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CapacitorAudioRecorder } from '@capgo/capacitor-audio-recorder';
import { Filesystem } from '@capacitor/filesystem';
import { StateService } from '../../services/state.service';
import { ToolService } from '../../services/tool.service';
export interface AudioRecording {
  uri?: string;
  blob?: Blob;
  duration: number;
}

type RecorderState = 'idle' | 'recording' | 'processing' | 'recorded';

@Component({
  selector: 'lib-audio-recorder',
  templateUrl: './audio-recorder.component.html',
  styleUrls: ['./audio-recorder.component.scss'],
  standalone: false,
})
export class AudioRecorderComponent implements OnDestroy {
  private translate = inject(TranslateService);

  /**
   *
   */
  constructor(private tools: ToolService, private state: StateService) {}
  @Output()
  readonly recordingCreated = new EventEmitter<AudioRecording>();

  readonly audioState = signal<RecorderState>('idle');

  readonly duration = signal(0);

  readonly amplitude = signal(0);

  readonly error = signal<string | null>(null);

  /**
   * URL used by the HTMLAudioElement for playback.
   *
   * On Web this is created from the returned Blob.
   *
   * On iOS/Android the URI returned by the native plugin
   * is used directly.
   */
  readonly audioUrl = signal<string | null>(null);

  readonly isPlaying = signal(false);

  private timer?: ReturnType<typeof setInterval>;

  private amplitudeTimer?: ReturnType<typeof setInterval>;

  private audio?: HTMLAudioElement;

  private recording?: AudioRecording;

  // ---------------------------------------------------------------------------
  // Recording
  // ---------------------------------------------------------------------------

  async startRecording(): Promise<void> {
    this.error.set(null);

    try {
      const permission = await CapacitorAudioRecorder.requestPermissions();

      if (permission.recordAudio !== 'granted') {
        this.error.set(this.translate.instant('audio.permissionDenied'));

        return;
      }

      // Remove a previous recording when starting a new one.
      this.clearPlayback();

      this.duration.set(0);
      this.amplitude.set(0);

      await CapacitorAudioRecorder.startRecording({
        sampleRate: 44100,
        bitRate: 128000,
      });

      this.audioState.set('recording');

      this.startTimer();
      this.startAmplitudeVisualizer();
    } catch (error) {
      console.error('Could not start recording', error);

      this.error.set(this.translate.instant('audio.startError'));

      this.audioState.set('idle');

      this.stopTimer();
      this.stopAmplitudeVisualizer();
    }
  }

  async stopRecording(): Promise<void> {
    if (this.audioState() !== 'recording') {
      return;
    }

    this.audioState.set('processing');

    this.stopTimer();
    this.stopAmplitudeVisualizer();

    try {
      const result = await CapacitorAudioRecorder.stopRecording();
      let blob: Blob;
      if (result.blob) {
        // Web
        blob = result.blob;
      } else if (result.uri) {
        // iOS / Android
        // Android sample path: file:///data/user/0/com.schweizermedia.baulogistik/cache/capacitor-audio-recorder/recording-20260909-084814.m4a
        // iOS sample path: file:///private/var/mobile/Containers/Data/Application/59D5C069-8304-404D-8771-E701F6BA8847/tmp/CapacitorAudioRecorder/2F2B01FE-EBA1-4C65-AE02-281ED1429A4F.m4a
        // Capacitor.convertFileSrc() is not able to read/convert /tmp-paths of ios which is the reason why we read the file manually
        blob = await this.fileUriToBlob(result.uri);
      } else {
        throw new Error(this.translate.instant('audio.noFile'));
      }

      const recording: AudioRecording = {
        uri: result.uri,
        blob: blob,
        duration: result.duration ?? this.duration() * 1000,
      };

      this.recording = recording;

      this.duration.set(Math.round(recording.duration / 1000));

      this.createPlaybackUrl(recording);

      this.audioState.set('recorded');
    } catch (error) {
      console.error('Could not stop recording', error);
      if (this.state.isApplicationAdmin)
        this.tools.showInfoToast(JSON.stringify(error));
      this.error.set(this.translate.instant('audio.stopError'));

      this.audioState.set('idle');
    } finally {
      this.amplitude.set(0);
    }
  }

  // ---------------------------------------------------------------------------
  // Delete / re-record
  // ---------------------------------------------------------------------------

  async deleteRecording(): Promise<void> {
    this.clearPlayback();

    this.recording = undefined;

    this.duration.set(0);
    this.amplitude.set(0);
    this.error.set(null);

    this.audioState.set('idle');
  }

  public analyzeRecording(): void {
    this.recordingCreated.emit(this.recording);
  }

  async recordAgain(): Promise<void> {
    await this.deleteRecording();
    await this.startRecording();
  }

  // ---------------------------------------------------------------------------
  // Playback
  // ---------------------------------------------------------------------------

  async togglePlayback(): Promise<void> {
    if (!this.audio || !this.audioUrl()) {
      return;
    }

    try {
      if (this.isPlaying()) {
        this.audio.pause();
        this.isPlaying.set(false);
        return;
      }

      await this.audio.play();

      this.isPlaying.set(true);
    } catch (error) {
      console.error('Could not play recording', error);

      this.error.set('Die Aufnahme konnte nicht abgespielt werden.');
    }
  }

  // ---------------------------------------------------------------------------
  // Timer
  // ---------------------------------------------------------------------------

  private startTimer(): void {
    this.stopTimer();

    this.timer = setInterval(() => {
      this.duration.update((value) => value + 1);
    }, 1000);
  }

  private stopTimer(): void {
    if (!this.timer) {
      return;
    }

    clearInterval(this.timer);
    this.timer = undefined;
  }

  // ---------------------------------------------------------------------------
  // Live amplitude
  // ---------------------------------------------------------------------------

  private startAmplitudeVisualizer(): void {
    this.stopAmplitudeVisualizer();

    this.amplitudeTimer = setInterval(async () => {
      if (this.audioState() !== 'recording') {
        return;
      }

      try {
        const result = await CapacitorAudioRecorder.getCurrentAmplitude();

        this.amplitude.set(Math.min(1, Math.max(0, result.value)));
      } catch {
        // The visualizer should never break the recording.
      }
    }, 80);
  }

  private stopAmplitudeVisualizer(): void {
    if (!this.amplitudeTimer) {
      return;
    }

    clearInterval(this.amplitudeTimer);
    this.amplitudeTimer = undefined;
  }

  // ---------------------------------------------------------------------------
  // Playback URL
  // ---------------------------------------------------------------------------

  private createPlaybackUrl(recording: AudioRecording): void {
    this.clearPlayback();

    if (recording.blob) {
      const url = URL.createObjectURL(recording.blob);
      this.audioUrl.set(url);
      this.createAudioElement(url);
      return;
    }

    if (recording.uri) {
      this.audioUrl.set(recording.uri);
      this.createAudioElement(recording.uri);
    }
  }

  private createAudioElement(url: string): void {
    this.audio = new Audio(url);

    this.audio.preload = 'metadata';

    this.audio.addEventListener('ended', () => {
      this.isPlaying.set(false);
    });

    this.audio.addEventListener('pause', () => {
      this.isPlaying.set(false);
    });

    this.audio.addEventListener('play', () => {
      this.isPlaying.set(true);
    });
  }

  private clearPlayback(): void {
    if (this.audio) {
      this.audio.pause();
      this.audio.src = '';
      this.audio = undefined;
    }

    const url = this.audioUrl();

    if (url?.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }

    this.audioUrl.set(null);
    this.isPlaying.set(false);
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  formatDuration(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds
      .toString()
      .padStart(2, '0')}`;
  }

  private async fileUriToBlob(uri: string): Promise<Blob> {
    // file:///... -> /...
    const path = uri.startsWith('file://')
      ? uri.substring('file://'.length)
      : uri;

    const result = await Filesystem.readFile({
      path,
    });

    // Capacitor liefert bei Binärdateien Base64
    const base64 = result.data as string;

    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    return new Blob([bytes], {
      type: 'audio/mp4',
    });
  }

  /**
   * Generates bars for the live visualizer.
   *
   * We intentionally don't use the actual audio waveform here.
   * The bars are driven by the microphone amplitude.
   */
  getVisualizerBars(): number[] {
    const value = this.amplitude();

    const bars = 28;

    return Array.from({ length: bars }, (_, index) => {
      const center = Math.abs(index - bars / 2) / (bars / 2);

      const variation = Math.sin(index * 1.7) * 0.12;

      const height = 0.25 + value * (0.75 - center * 0.3) + variation;

      return Math.max(0.12, Math.min(1, height));
    });
  }

  ngOnDestroy(): void {
    this.stopTimer();
    this.stopAmplitudeVisualizer();
    this.clearPlayback();
  }
}
